/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import '@/App.css';
import './i18n';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from '@/lib/auth';
import { ThemeProvider } from '@/lib/theme';
import { BrandProvider } from '@/lib/whitelabel';
import { AccessibilityProvider } from '@/lib/accessibility';
import { OnboardingStatusProvider } from '@/lib/onboardingStatus';
import { Toaster } from '@/components/ui/sonner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import SuperAdminSidebar from '@/components/dev/SuperAdminSidebar';
import LegalFooter from '@/components/layout/LegalFooter';
import { SessionWatermark } from '@/components/security/ProprietaryWatermark';
import { EulaGate } from '@/components/security/EulaGate';
import { AntiTamperGuard } from '@/components/security/AntiTamperGuard';
import { ServiceWorkerBridge } from '@/routes/guards';
import { AppRoutes } from '@/routes/AppRoutes';

/**
 * App — provider composition only.
 * Route table lives in `src/routes/AppRoutes.jsx`, guards in `src/routes/guards.jsx`.
 */
function App() {
  return (
    <div className="App">
      <BrandProvider>
        <ThemeProvider>
          <AccessibilityProvider>
            <AuthProvider>
              <BrowserRouter>
                <ServiceWorkerBridge />
                {/* IP protection layer: role-based anti-tamper + mandatory EULA gate for demo users */}
                <AntiTamperGuard />
                <EulaGate />
                <SessionWatermark />
                <OnboardingStatusProvider>
                  <ErrorBoundary scope="routes">
                    <AppRoutes />
                  </ErrorBoundary>
                  <LegalFooter />
                  <Toaster position="top-center" richColors />
                  <SuperAdminSidebar />
                </OnboardingStatusProvider>
              </BrowserRouter>
            </AuthProvider>
          </AccessibilityProvider>
        </ThemeProvider>
      </BrandProvider>
    </div>
  );
}

export default App;
